import React, { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useCategoryOptions,
  useSubcategoryOptions,
} from "@features/cx/consumerManagement/hooks";
import { useOwnerTypeChoices } from "@features/cx/disconnect/hooks";

interface MeterActivationFilterProps {
  form: UseFormReturn<any>;
  onApply: (data: any) => void;
  onReset: () => void;
}

export const MeterActivationFilter: React.FC<MeterActivationFilterProps> = ({
  form,
  onApply,
  onReset,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const watchCategory = form.watch("category");

  // Fetch Categories
  const { data: categoriesData, isLoading: isLoadingCategories } =
    useCategoryOptions(Number(remoteUtilityId));

  const categoryOptions = useMemo(() => {
    return (
      categoriesData?.result?.map((cat: any) => ({
        label: cat.name,
        value: cat.code,
      })) || []
    );
  }, [categoriesData]);

  // Fetch Sub Categories based on selected category
  const { data: subCategoriesData, isLoading: isLoadingSubCategories } =
    useSubcategoryOptions(Number(remoteUtilityId), watchCategory);

  const subCategoryOptions = useMemo(() => {
    return (
      subCategoriesData?.result?.map((sub: any) => ({
        label: sub.name,
        value: sub.code,
      })) || []
    );
  }, [subCategoriesData]);

  // Fetch Owner Types (Account Types)
  const { data: ownerTypesData, isLoading: isLoadingOwnerTypes } = useOwnerTypeChoices();

  const ownerTypeOptions = useMemo(() => {
    return (
      ownerTypesData?.result?.map((item: any) => ({
        label: item.value,
        value: item.key,
      })) || []
    );
  }, [ownerTypesData]);

  const fields: FormField[] = [
    {
      name: "category",
      label: "Category",
      type: "select",
      placeholder: isLoadingCategories ? "Loading..." : "Select Category",
      options: categoryOptions,
    },
    {
      name: "sub_category",
      label: "Sub Category",
      type: "select",
      placeholder: !watchCategory
        ? "Select Category first"
        : isLoadingSubCategories
        ? "Loading..."
        : "Select Sub Category",
      options: subCategoryOptions,
      disabled: !watchCategory,
    },
    {
      name: "accountType",
      label: "Account Type",
      type: "select",
      placeholder: isLoadingOwnerTypes ? "Loading..." : "Select Account Type",
      options: ownerTypeOptions,
    },
  ];

  const handleReset = () => {
    form.reset({
      category: "",
      sub_category: "",
      accountType: "",
    });
    onReset();
  };

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={fields}
        form={form}
        gridColumns={3}
        responsiveColumns={{ sm: 1, md: 2, lg: 3 }}
        onSubmit={onApply}
        onReset={handleReset}
        config={{
          showResetButton: true,
          resetButtonText: "Reset",
          submitButtonText: "Apply Filters",
          classes: {
            buttonContainer: "flex justify-end gap-2 pt-2 border-t",
          },
        }}
      />
    </div>
  );
};
